import { MessageDescriptor } from '@lingui/core';
import { defineMessage } from '@lingui/core/macro';
import { ErrorCode } from '@luminovo/api-contracts';

export const errorCodeTranslations: Record<ErrorCode, MessageDescriptor> = {
    'additional_service.english_name_cannot_be_empty': defineMessage({
        message: 'English name cannot be empty',
    }),
    'additional_service.german_name_cannot_be_empty': defineMessage({
        message: 'German name cannot be empty',
    }),
    'assembly.bom_file_not_found': defineMessage({
        message: 'No BOM file found for assembly.',
    }),
    'supplier_contact.cannot_edit_invited_supplier_contact': defineMessage({
        message: `We currently don't support editing invited supplier contacts. Try creating a new one instead.`,
    }),
    'supplier_contact.email_invalid': defineMessage({
        message: `The email address is invalid. Please enter a valid email address.`,
    }),
    'assembly.designator_already_exists_among_siblings': defineMessage({
        message: 'Assembly name already exists among siblings.',
    }),
    'assembly.not_found': defineMessage({
        message: 'Assembly not found',
    }),
    'assembly.cannot_add_top_level_assembly_as_customer_user': defineMessage({
        message: 'You can only add subassemblies, no additional top-level assemblies',
    }),
    'assembly.ipn_already_exists': defineMessage({
        message: 'Cannot associate the IPN with this assembly. Its already linked to another assembly.',
    }),
    'bom_export.cannot_delete_default_template': defineMessage({
        message: 'Cannot delete default BOM export template',
    }),
    'bom_export.cannot_delete_last_template': defineMessage({
        message: 'Cannot delete last BOM export template',
    }),
    'bom_export.cannot_update_default_status_of_default_template': defineMessage({
        message: 'Cannot update default status of default BOM export template',
    }),
    'bom_export.should_contain_at_least_one_column': defineMessage({
        message: 'BOM export template should contain at least one column',
    }),
    'bom_export.template_with_name_already_exists': defineMessage({
        message: 'There already exists a BOM export template with this name',
    }),
    'bom_import.duplicate_designators': defineMessage({
        message: 'Designator already exists.',
    }),
    'bom_import.cannot_read_file': defineMessage({
        message:
            'The uploaded file could not be read. Make sure it is not corrupted, for example by opening the file in Excel and saving it again.',
    }),
    'bom_import.bom_file_too_big': defineMessage({
        message:
            'The BOM is too large to process at once. Consider splitting it up while we work on scaling the import process to accommodate larger files.',
    }),
    // rust-workspace/domain-error/src/quotation.rs
    'calculation_assembly_cost.immutable_lock': defineMessage({
        message: 'Calculation assembly cost is locked and cannot be modified',
    }),
    'calculation_assembly_cost.internal': defineMessage({
        message: 'Internal calculation assembly cost error',
    }),
    'calculation_assembly_cost.locked_cell_cannot_be_modified': defineMessage({
        message: 'This cell is locked and cannot be modified',
    }),
    'calculation_assembly_cost.not_found': defineMessage({
        message: 'Calculation assembly cost not found',
    }),
    'calculation_assembly_details.not_found': defineMessage({
        message: 'Calculation assembly details not found',
    }),
    'calculation_cost.internal_error': defineMessage({
        message: 'Internal calculation cost error',
    }),
    'calculation.currency_error': defineMessage({
        message: 'Calculation currency error',
    }),
    'calculation.driver_resolver_error': defineMessage({
        message: 'Calculation driver resolver error',
    }),
    'calculation.internal_error': defineMessage({
        message: 'Internal calculation error',
    }),
    'calculation_template.not_found': defineMessage({
        message: 'Calculation template not found',
    }),
    'costed_bom_export.no_sourcing_scenarios': defineMessage({
        message:
            'Cannot export costed BOM: the project has no sourcing scenarios. Please create a sourcing scenario first.',
    }),
    'costed_bom_template.cannot_delete_default_template': defineMessage({
        message: 'Cannot delete default costed bom export template',
    }),
    'costed_bom_template.cannot_delete_last_template': defineMessage({
        message: 'Cannot delete last costed bom export template',
    }),
    'costed_bom_template.cannot_update_default_status_of_default_template': defineMessage({
        message: 'Cannot update default status of default costed bom export template',
    }),
    'costed_bom_template.should_contain_at_least_one_column': defineMessage({
        message: 'Costed bom export template should contain at least one column',
    }),
    'costed_bom_template.template_with_name_already_exists': defineMessage({
        message: 'There already exists a costed bom export template with this name',
    }),
    'customer.billing_address_incomplete': defineMessage({
        message: 'Billing address is incomplete. Please contact your EMS to add this information.',
    }),
    'customer.vat_id_incomplete': defineMessage({
        message: 'VAT ID is incomplete. Please contact your EMS to add this information.',
    }),
    'customer.name_unavailable': defineMessage({
        message: 'Customer with this name already exists.',
    }),
    'customer.not_found': defineMessage({
        message: 'Customer not found',
    }),
    'customer.payload_vat_identification_number_missing': defineMessage({
        message: 'VAT identification number and type must be set together.',
    }),
    'customer.payload_vat_identification_type_invalid': defineMessage({
        message: 'VAT identification number and type must be set together.',
    }),
    'customer.number_already_exists': defineMessage({
        message: 'Customer with this number already exists.',
    }),
    'customer.has_referenced_demands': defineMessage({
        message: 'Cannot delete customer referenced in a demand.',
    }),
    'demand.ambiguous_participant_site_number': defineMessage({
        message: 'Site number is ambiguous, multiple sites have this site number',
    }),
    'demand.prototype_demand_scenario_date_not_in_future': defineMessage({
        message: 'Prototype demand scenario date not in future',
    }),
    'demand.invalid_prototype_demand_quantity': defineMessage({
        message: 'Invalid prototype demand quantity',
    }),
    'demand.invalid_dates': defineMessage({
        message: 'Production start date must on or before delivery date',
    }),
    'demand.invalid_delivery_dates': defineMessage({
        message: 'Delivery start date must be on or before delivery end date',
    }),
    'demand.invalid_demand_scenario_year': defineMessage({
        message: 'Invalid demand scenario year',
    }),
    'demand.invalid_demand_scenario_end_year': defineMessage({
        message: 'Invalid demand scenario end year',
    }),
    'demand.no_annual_demands': defineMessage({ message: 'No annual demands' }),
    'demand.only_site_number_or_site_id_required': defineMessage({
        message: 'Only one of site number or site id should be given',
    }),
    'demand.only_customer_number_or_customer_id_required': defineMessage({
        message: 'Only one of customer number or customer id should be given',
    }),
    'demand.invalid_annual_demand': defineMessage({
        message: 'Invalid annual demand',
    }),
    'demand.multiple_annual_demands_for_one_year': defineMessage({
        message: 'Multiple annual demands for one year',
    }),
    'demand.missing_annual_demand': defineMessage({
        message: 'Missing annual demand',
    }),
    'demand.more_than_three_delivery_batch_sizes': defineMessage({
        message: "Can't have more than 3 delivery batch sizes",
    }),
    'demand.delivery_batch_size_out_of_bounds': defineMessage({
        message: 'Delivery batch sizes must be between 1 and 100_000_000 (inclusive)',
    }),
    'demand.invalid_assembly_id': defineMessage({
        message: 'The assembly id specified is not a valid UUID',
    }),
    // rust-workspace/domain-error/src/manufacturing.rs
    'driver.name_already_exists': defineMessage({
        message: 'Driver name already exists',
    }),
    'driver.name_already_in_use': defineMessage({
        message: 'Driver name already exists',
    }),
    'driver.name_must_not_be_empty': defineMessage({
        message: 'Driver name must not be empty',
    }),
    'driver.name_must_be_alphanumeric': defineMessage({
        message: 'Driver name must be alphanumeric including whitespace only',
    }),
    'driver.first_char_must_be_letter': defineMessage({
        message: 'First character of driver name must be a letter',
    }),
    'driver.name_is_reserved_keyword': defineMessage({
        message: 'Driver name is reserved as a keyword',
    }),
    'driver.name_is_system_driver': defineMessage({
        message: 'Driver name is system driver',
    }),
    'driver.invalid_lexorank': defineMessage({
        message: 'Invalid driver rank',
    }),
    'driver.invalid_package_name': defineMessage({
        message: 'Package name in filter does not exist',
    }),
    // rust-workspace/domain-error/src/quotation.rs
    'expense_configuration.invalid_rank': defineMessage({
        message: 'Invalid expense configuration rank',
    }),
    'expense_configuration.not_found': defineMessage({
        message: 'Expense configuration not found',
    }),
    'expense.internal_number_already_exists': defineMessage({
        message: 'Expense internal number already exists',
    }),
    'expense.name_already_exists': defineMessage({
        message: 'Expense name already exists',
    }),
    'expense.not_found': defineMessage({
        message: 'Expense not found',
    }),
    'email_template.name.already_exists': defineMessage({
        message: `This email template name is already in use. Please choose a different name.`,
    }),
    'email_template.suppliers.conflict': defineMessage({
        message: `There is already an email template assign to one of the selected suppliers.`,
    }),
    'integration.error': defineMessage({
        message:
            'Oops! We encountered an issue with the API integration. Please double-check your API key and try again. If the issue continues to persist, please contact our customer support.',
    }),
    'integration.invalid_api_key': defineMessage({
        message: 'Invalid API key. Please double-check your API key and try again.',
    }),
    'integration.invalid_credentials': defineMessage({
        message: 'Invalid credentials. Please verify your login information and try again.',
    }),
    'integration.invalid_settings': defineMessage({
        message: 'Invalid settings. Please review your integration settings and make necessary corrections.',
    }),
    'integration.rate_limited': defineMessage({
        message:
            'Rate limited. You have exceeded the rate limit. Please wait a while or contact our customer support for assistance.',
    }),
    'invite.email_already_exists': defineMessage({
        message: 'A user with this email address already exists',
    }),
    'invite.invalid_user_invite': defineMessage({
        message: 'The invitation link is invalid or has expired. Please request another invitation.',
    }),
    'invite.invite_already_sent': defineMessage({
        message: 'An invitation link has already been sent',
    }),
    'ipn.no_such_form_and_fit': defineMessage({
        message: 'No such form and fit',
    }),
    'ipn.not_found': defineMessage({
        message: 'IPN not found',
    }),
    'ipn.deletion_limit_exceeded': defineMessage({
        message:
            'Cannot delete IPNs: exceeds maximum allowed limit. You can adjust the limit in the component settings.',
    }),
    // rust-workspace/domain-error/src/quotation.rs
    'activity_configuration.invalid_rank': defineMessage({
        message: 'Invalid activity configuration rank',
    }),
    'activity_configuration.not_found': defineMessage({
        message: 'Activity configuration not found',
    }),
    'activity.internal_number_already_exists': defineMessage({
        message: 'Activity internal number already exists',
    }),
    'activity.name_already_exists': defineMessage({
        message: 'Activity name already exists',
    }),
    // rust-workspace/domain-error/src/quotation.rs
    'activity.not_found': defineMessage({
        message: 'Activity not found',
    }),
    'manufacturing_assembly_details.driver_does_not_use_sourcing_field': defineMessage({
        message: 'Driver does not use sourcing field',
    }),
    // rust-workspace/domain-error/src/quotation.rs
    'manufacturing_assembly_details.not_found': defineMessage({
        message: 'Manufacturing assembly details not found',
    }),
    'manufacturing_assembly_details.panel_factor_less_than_one': defineMessage({
        message: 'Panel factor cannot be less than 1',
    }),
    // rust-workspace/domain-error/src/quotation.rs
    'manufacturing_scenario.internal': defineMessage({
        message: 'Internal manufacturing scenario error',
    }),
    'manufacturing_scenario.invalid_batch_sizes': defineMessage({
        message: 'Invalid batch sizes for manufacturing scenario',
    }),
    'manufacturing_scenario.name_already_exists': defineMessage({
        message: 'Manufacturing scenario name already in use',
    }),
    'manufacturing_scenario.not_found': defineMessage({
        message: 'Manufacturing scenario not found',
    }),
    'manufacturing_scenario_template_instance.illegal_template_reference': defineMessage({
        message: 'Illegal template reference in manufacturing scenario',
    }),
    'manufacturing_scenario_template_instance.invalid_rank': defineMessage({
        message: 'Invalid rank for manufacturing scenario template instance',
    }),
    'manufacturing_scenario_template_instance.not_found': defineMessage({
        message: 'Manufacturing scenario template instance not found',
    }),
    'manufacturing_scenario_template.name_already_exists': defineMessage({
        message: 'Template name already in use',
    }),
    // rust-workspace/domain-error/src/manufacturing.rs
    'manufacturing_scenario_template.not_found': defineMessage({
        message: 'Manufacturing scenario template not found',
    }),
    'monitoring.export.not_ready': defineMessage({
        message: 'Export is not ready yet. Please wait for indexing to complete.',
    }),
    'panel.generation.pcb_width_exceeds_panel': defineMessage({
        message: 'PCB is too large to fit the panel preferences..',
    }),
    'panel.generation.pcb_height_exceeds_panel': defineMessage({
        message: 'PCB is too large to fit the panel preferences..',
    }),
    'panel.generation.unknown_error': defineMessage({
        message: 'PCB is too large to fit the panel preferences..',
    }),
    'payload.email_cannot_be_empty': defineMessage({
        message: 'Email is required',
    }),
    'payload.first_name_cannot_be_empty': defineMessage({
        message: 'First name is required',
    }),
    'payload.last_name_cannot_be_empty': defineMessage({
        message: 'Last name is required',
    }),
    'payload.password_cannot_contain_personal_data': defineMessage({
        message: 'Password cannot contain personal information such as name or email address.',
    }),
    'payload.password_mismatch': defineMessage({
        message: 'Passwords do not match',
    }),
    'payload.weak_password': defineMessage({
        message:
            'Password not strong enough. It must be least 8 characters long and contain lower case, upper case, numeric, and special characters.',
    }),
    'payload.customer_company_name_cannot_be_empty': defineMessage({
        message: 'Company name is required',
    }),
    'payload.vat_identification_number_cannot_be_empty': defineMessage({
        message: 'VAT identification number is required',
    }),
    'pcb_specification_importer.pcb_specification.error_file_required': defineMessage({
        message: `We couldn't find the PDF file in the request. Please try again.`,
    }),
    'pcb_specification_importer.pcb_specification.error_extracting_pcb_specification': defineMessage({
        message: `We couldn't extract any information from the PDF file. Please check its content and try again. If the issue persists, please contact our customer support.`,
    }),
    'pcb_specification_csv_export.no_visible_fields': defineMessage({
        message: 'No visible fields selected for PCB specification CSV export',
    }),
    'pcb_specification_csv_export.validated_token_missing': defineMessage({
        message: 'Validated token is missing for PCB specification CSV export',
    }),
    'pcb_specification_preferences.visible_fields_required': defineMessage({
        message: 'At least one visible field is required for PCB specification preferences',
    }),
    'purchase_order.conflict': defineMessage({
        message: 'This purchase order ID is already in use. Please use a different ID.',
    }),
    // rust-workspace/domain-error/src/quotation.rs
    'quotation.document_already_exists': defineMessage({
        message: 'Quotation document already exists',
    }),
    'quotation.internal': defineMessage({
        message: 'Internal quotation error',
    }),
    'quotation.unauthorized': defineMessage({
        message: 'You are not authorized to perform this quotation action',
    }),
    'pnp_file.not_found': defineMessage({
        message: 'Pick and Place file not found',
    }),
    'pnp_importer.column_missing': defineMessage({
        message: 'One of the required columns is missing',
    }),
    'pnp_importer.csv': defineMessage({
        message: 'There was an error uploading your file. Please try again',
    }),
    // rust-workspace/domain-error/src/pnp_importer.rs
    'pnp_importer.empty_pnp_file': defineMessage({
        message: 'The PnP file is empty',
    }),
    'pnp_importer.invalid_mapping': defineMessage({
        message: 'Mapping is invalid',
    }),
    'pnp_importer.multiple_mapping': defineMessage({
        message: 'Multiple mapping',
    }),
    'pnp_importer.invalid_rfq_state': defineMessage({
        message: 'The current project status does not allow uploading or modifying PnP file',
    }),
    'pnp_item.not_found': defineMessage({
        message: 'PnP item not found',
    }),
    'project_creation.assembly_name_invalid_length': defineMessage({
        message: 'Assembly name has an invalid length',
    }),
    'project_creation.delivery_start_date_must_be_before_end_date': defineMessage({
        message: 'Delivery start date must be before end date',
    }),
    'project_creation.demand_group_description_invalid_length': defineMessage({
        message: 'Demand group description has an invalid length',
    }),
    'project_creation.demand_group_name_invalid_length': defineMessage({
        message: 'Demand group name has an invalid length',
    }),
    'project_creation.duplicate_sourcing_scenario_name': defineMessage({
        message: 'Sourcing scenario names must be unique within a project',
    }),
    'project_creation.existing_assembly_must_have_same_customer': defineMessage({
        message: 'Existing assemblies must belong to the project customer',
    }),
    'project_creation.internal_project_number_invalid_length': defineMessage({
        message: 'Internal project number has an invalid length',
    }),
    'project_creation.lower_estimate_must_not_be_negative': defineMessage({
        message: 'Volume estimate lower bound must not be negative',
    }),
    'project_creation.project_group_name_invalid_length': defineMessage({
        message: 'Project name has an invalid length',
    }),
    'project_creation.sourcing_scenario_name_invalid_length': defineMessage({
        message: 'Sourcing scenario name has an invalid length',
    }),
    'project_creation.upper_estimate_must_not_be_negative': defineMessage({
        message: 'Volume estimate upper bound must not be negative',
    }),
    'quote_importer.extraction': defineMessage({
        message: `Extraction failed`,
    }),
    'quote_importer.general': defineMessage({
        message: `Import failed`,
    }),
    'quote_importer.no_supplier': defineMessage({
        message: `No supplier found`,
    }),
    'quote_importer.negative_stock': defineMessage({
        message: `Stock must not be negative`,
    }),
    'quote_importer.invalid_factory_lead_time': defineMessage({
        message: `Factory lead time must be greater than 0 days`,
    }),
    'quote_importer.negative_factory_quantity': defineMessage({
        message: `Factory quantity must not be negative`,
    }),
    'quote_importer.invalid_moq': defineMessage({
        message: `MOQ must be greater than 0`,
    }),
    'quote_importer.invalid_mpq': defineMessage({
        message: `MPQ must be greater than 0`,
    }),
    'quote_importer.negative_unit_price': defineMessage({
        message: `Unit price must not be negative`,
    }),
    'quote_importer.invalid_lead_time': defineMessage({
        message: `Lead time must be greater than 0 days`,
    }),
    'quote_importer.invalid_unit_of_measurement': defineMessage({
        message: `Unit of measurement cannot be <= 0`,
    }),
    'quote_importer.part_not_found': defineMessage({
        message: `Part not found`,
    }),
    'quote_importer.unsupported_part_type': defineMessage({
        message: `Generic part is not supported`,
    }),
    'quote_importer.suppliers_not_equal': defineMessage({
        message: `An offer's supplier and stock location is different from the provided main one`,
    }),
    'llm.custom_part_offer.error_extracting_offer': defineMessage({
        message: `We couldn't extract any information from the document. Please check its content and try again. If the issue persists, please contact our customer support.`,
    }),
    'llm.custom_part_offer.error_file_required': defineMessage({
        message: `We couldn't find the file in the request. Please try again.`,
    }),
    'llm.custom_part_offer.error_incomplete_response': defineMessage({
        message: `We couldn't fully process the document. Please try again. If the issue persists, please contact our customer support.`,
    }),
    'llm.custom_part_offer.error_streaming_file': defineMessage({
        message: `We failed to upload the file because it was too large. We can handle PDF files with up to 100 pages. Try uploading a smaller file.`,
    }),
    'llm.custom_part_offer.error_too_many_items': defineMessage({
        message: `The document contains too many items to extract at once. Try uploading a document with fewer items.`,
    }),
    'llm.off_the_shelf_offer.error_extracting_offer': defineMessage({
        message: `We couldn't extract any information from the document. Please check its content and try again. If the issue persists, please contact our customer support.`,
    }),
    'llm.off_the_shelf_offer.error_file_required': defineMessage({
        message: `We couldn't find the file in the request. Please try again.`,
    }),
    'llm.off_the_shelf_offer.error_incomplete_response': defineMessage({
        message: `We couldn't fully process the document. Please try again. If the issue persists, please contact our customer support.`,
    }),
    'llm.off_the_shelf_offer.error_too_many_line_items': defineMessage({
        message: `The document contains too many line items to extract at once. Try uploading a document with fewer items.`,
    }),
    'rfq.cannot_create_customer_as_customer_user': defineMessage({
        message: 'Cannot create a customer as customer user',
    }),
    'rfq.cannot_create_rfq_for_different_customer': defineMessage({
        message: 'Cannot create a project for a different customer',
    }),
    'rfq.cannot_update_archived': defineMessage({
        message: 'An archived project cannot be updated',
    }),
    'rfq.customer_does_not_exist': defineMessage({
        message: 'The selected customer does not exist',
    }),
    'rfq.customer_user_has_no_customer': defineMessage({
        message: 'Customer user has no customer',
    }),
    'rfq.delete_rfq_with_sales_order_prohibited': defineMessage({
        message:
            'Cannot delete a project where the customer has purchased a sales order. Please contact customer support to confirm deletion of both the project and sales order',
    }),
    'rfq.delete_rfq_with_part_in_negotiation': defineMessage({
        message:
            'Cannot delete a project where a part is used in a negotiation. Please delete the negotiation first before deleting the project',
    }),
    'rfq.duplicate_order_sizes': defineMessage({
        message: 'Order sizes for each scenario date must be unique',
    }),
    // rust-workspace/domain-error/src/rfq.rs
    'rfq.duplication_failed': defineMessage({
        message: 'Project duplication failed',
    }),
    // rust-workspace/domain-error/src/rfq.rs
    'rfq.empty_update_prohibited': defineMessage({
        message: 'Empty project updates are prohibited',
    }),
    'rfq.invalid_assembly_name': defineMessage({
        message: 'Invalid assembly name',
    }),
    // rust-workspace/domain-error/src/rfq.rs
    'rfq.invalid_creator': defineMessage({
        message: 'Invalid project creator',
    }),
    'rfq.duplicate_assembly_names': defineMessage({
        message: 'Please choose unique assembly names, duplicates are not supported.',
    }),
    'rfq.invalid_customer_name': defineMessage({
        message: 'Invalid customer name',
    }),
    'rfq.invalid_name': defineMessage({
        message: 'Invalid project name',
    }),
    // rust-workspace/domain-error/src/rfq.rs
    'rfq.invalid_operation': defineMessage({
        message: 'Invalid project operation',
    }),
    'rfq.invalid_order_date': defineMessage({
        message: 'Invalid order scenario date',
    }),
    'rfq.invalid_order_size': defineMessage({
        message: 'Invalid order size',
    }),
    'rfq.invalid_rfq_number': defineMessage({
        message: 'Invalid project number',
    }),
    // rust-workspace/domain-error/src/rfq.rs
    'rfq.invalid_status': defineMessage({
        message: 'Invalid project status',
    }),
    'rfq.invalid_status_for_archiving': defineMessage({
        message: 'Project cannot be archived in its current status',
    }),
    'rfq.invalid_volume_estimate_bounds': defineMessage({
        message: 'Invalid volume estimate',
    }),
    // rust-workspace/domain-error/src/rfq.rs
    'rfq.invalid_workflow_for_operation': defineMessage({
        message: 'This operation is not supported for the current workflow',
    }),
    // rust-workspace/domain-error/src/rfq.rs
    'rfq.missing_shipping_tracking_link': defineMessage({
        message: 'Missing shipping tracking link',
    }),
    'rfq.new_customer_name_unavailable': defineMessage({
        message: 'The name for your new customer is unavailable',
    }),
    'rfq.not_found': defineMessage({
        message: 'The project was not found',
    }),
    'rfq.request_in_draft_only_allowed_if_customer_portal_is_enabled': defineMessage({
        message: 'Request in draft only allowed if customer portal is enabled',
    }),
    'rfq.unauthorized': defineMessage({
        message: 'You are not authorized to perform this action',
    }),
    // rust-workspace/domain-error/src/rfq.rs
    'rfq.unauthorized_status_update': defineMessage({
        message: 'You are not authorized to update this project status',
    }),
    'rfq.unauthorized_to_create': defineMessage({
        message: 'You are not authorized to create a project',
    }),
    unauthorized: defineMessage({
        message: 'You are not authorized to perform this action',
    }),
    forbidden: defineMessage({
        message: 'You are not authorized to perform this action',
    }),
    // The backend sends this bare code for any database constraint violation (a duplicate name or
    // IPN, most often) and for concurrent modifications, without saying which. Keep the wording
    // general enough to cover both, and point at the retry that resolves the concurrent case.
    conflict: defineMessage({
        message: 'This change conflicts with existing data. Check for duplicate values, then try again.',
    }),
    internal_error: defineMessage({
        message: 'Internal server error',
    }),
    'rfq.workflow_changes_only_allowed_by_ems': defineMessage({
        message: 'Workflow changes are only allowed by the EMS',
    }),
    // rust-workspace/domain-error/src/role.rs
    'role.calculation_module_restricted_for_manufacturing_lite': defineMessage({
        message: 'Calculation module is restricted for manufacturing lite',
    }),
    'role.manage_manufacturing_restricted_for_manufacturing_lite': defineMessage({
        message: 'Managing manufacturing is restricted for manufacturing lite',
    }),
    'role.manufacturing_module_restricted_for_calculation_without_manufacturing': defineMessage({
        message: 'Manufacturing module is restricted for calculation without manufacturing',
    }),
    // rust-workspace/domain-error/src/quotation.rs
    'scenario_combination.batch_size_not_found': defineMessage({
        message: 'Batch size not found in scenario combination',
    }),
    'scenario_combination.invalid_batch_size': defineMessage({
        message: 'Invalid batch size in scenario combination',
    }),
    'scenario_combination.invalid_sourcing_scenario': defineMessage({
        message: 'Invalid sourcing scenario in scenario combination',
    }),
    'scenario_combination.manufacturing_scenario_not_found': defineMessage({
        message: 'Manufacturing scenario not found in scenario combination',
    }),
    'scenario_combination.not_found': defineMessage({
        message: 'Scenario combination not found',
    }),
    'scenario_combination.sourcing_scenario_not_found': defineMessage({
        message: 'Sourcing scenario not found in scenario combination',
    }),
    'self_signup_settings.contact_person_must_be_from_ems': defineMessage({
        message: 'The default contact person must be a member of the EMS.',
    }),
    'self_signup_settings.contact_person_not_found': defineMessage({
        message: 'The default contact person does not exist.',
    }),
    'self_signup_settings.invalid_color_scheme': defineMessage({
        message: 'The color scheme is invalid. It must consist of hex codes.',
    }),
    'self_signup_settings.invalid_organization_logo': defineMessage({
        message: 'The link to the organization logo is invalid.',
    }),
    // rust-workspace/domain-error/src/site.rs
    'site.cannot_create_sourcing_site': defineMessage({
        message: 'Cannot create sourcing site',
    }),
    'site.cannot_delete_default_inventory_site': defineMessage({
        message: 'Default inventory site cannot be deleted',
    }),
    'site.cannot_delete_inventory_site_with_linked_inventory_offers': defineMessage({
        message: 'Inventory site with linked inventory offers cannot be deleted',
    }),
    'site.cannot_remove_default_setting_from_last_inventory_site': defineMessage({
        message: 'There must be at least one default inventory site',
    }),
    // rust-workspace/domain-error/src/site.rs
    'site.cannot_remove_default_setting_from_last_site_with_tag': defineMessage({
        message: 'Cannot remove default setting from the last site with this tag',
    }),
    'site.cannot_remove_tag': defineMessage({
        message: 'Cannot remove tag from site',
    }),
    'site.invalid_default_site_setting': defineMessage({
        message: 'Non-inventory sites cannot be used as default inventory site',
    }),
    'site.must_have_at_least_one_tag': defineMessage({
        message: 'Site must have at least one tag',
    }),
    'site.name_already_exists': defineMessage({
        message: 'Site name already in use',
    }),
    // rust-workspace/domain-error/src/site.rs
    'site.not_found': defineMessage({
        message: 'Site not found',
    }),
    'site.payload_address_cannot_be_empty': defineMessage({
        message: 'Site address is required',
    }),
    'site.payload_city_cannot_be_empty': defineMessage({
        message: 'Site city is required',
    }),
    'site.payload_invalid_country_code': defineMessage({
        message: 'Invalid country code',
    }),
    'site.payload_name_cannot_be_empty': defineMessage({
        message: 'Site name is required',
    }),
    'site.payload_note_cannot_be_empty': defineMessage({
        message: 'Site note cannot be empty',
    }),
    'site.payload_postcode_cannot_be_empty': defineMessage({
        message: 'Site postcode is required',
    }),
    'site.site_number_already_exists': defineMessage({
        message: 'Site number already in use',
    }),
    'solution_configuration.not_found': defineMessage({
        message: 'The selected solution does not exist anymore.',
    }),
    'solution_configuration.invalid_offers': defineMessage({
        message: 'The selected solution is invalid or has expired. Please select another solution.',
    }),
    'solution_configuration.invalid_scrap_quantity': defineMessage({
        message: `Invalid scrap quantity`,
    }),
    'solution_configuration.no_design_items': defineMessage({
        message: 'The selected solution has no design items.',
    }),
    'solution_configuration.no_part_options': defineMessage({
        message: 'The selected solution has no part options.',
    }),
    'solution_configuration.solution_not_found': defineMessage({
        message: 'The selected solution does not exist anymore.',
    }),
    'solution_configuration.cannot_set_manual_status_on_automatic_solution': defineMessage({
        message: 'Cannot set manual status on automatic selected solution',
    }),
    'sourcing_line_item.could_not_find_offer_for_target_sourcing_line_item': defineMessage({
        message: 'Could not find offer for target sourcing line item',
    }),
    'sourcing_line_item.could_not_find_offer_for_origin_sourcing_line_item': defineMessage({
        message: 'Could not find offer for origin sourcing line item',
    }),
    'sourcing_line_item.could_not_find_origin_sourcing_line_item': defineMessage({
        message: 'Could not find origin sourcing line item',
    }),
    'sourcing_line_item.invalid_siblings': defineMessage({
        message: 'Sourcing line item has invalid siblings',
    }),
    'sourcing_line_item.trying_to_sync_line_item_without_offers': defineMessage({
        message: 'Cannot sync sourcing line item without offers',
    }),
    'sourcing_scenario.name_already_exists': defineMessage({
        message: 'Sourcing scenario name already in use',
    }),
    'sourcing_scenario.name_too_long': defineMessage({
        message: 'Sourcing scenario name must be 200 characters or fewer.',
    }),
    'sourcing_scenario.not_found': defineMessage({
        message: 'Sourcing scenario not found',
    }),
    'supplier_and_stock_location.already_exists': defineMessage({
        message: `Supplier name already in use`,
    }),
    'supplier.not_found': defineMessage({
        message: `Supplier not found`,
    }),
    'supplier.name.empty': defineMessage({
        message: `Supplier name is empty`,
    }),
    'supplier.name.already_exists': defineMessage({
        message: `Supplier name already in use`,
    }),
    'supplier.supplier_number.empty': defineMessage({
        message: `Supplier number is empty`,
    }),
    'supplier.supplier_number.already_exists': defineMessage({
        message: `Supplier number already in use`,
    }),
    'supplier.supplier_number.not_found': defineMessage({
        message: `Supplier number not found`,
    }),
    'delete_manual_supplier.internal': defineMessage({
        message: `Internal error deleting manual supplier`,
    }),
    'supplier_terms.not_found': defineMessage({
        message: `Supplier terms not found`,
    }),
    'supplier_terms.payment_due_days_constraint_violation': defineMessage({
        message: `Net payment due days must be greater than early payment due days`,
    }),
    'supplier_terms.already_exists': defineMessage({
        message: `Supplier terms for this site have already been added`,
    }),
    'sourcing_scenario.missing_name': defineMessage({
        message: `Name provided is an empty string`,
    }),
    'sourcing_scenario.demand_cannot_be_zero': defineMessage({
        message: `Sourcing scenario demand must be greater than 0`,
    }),
    'sourcing_scenario.empty_assembly_quantities': defineMessage({
        message: `At least one demand must be selected`,
    }),
    'sourcing_scenario.invalid_abc_classification_settings': defineMessage({
        message: `ABC classification percentages must sum to 100 (both cost and items)`,
    }),
    'sourcing_template.unauthorized': defineMessage({
        message: `You don't have permission to access this template`,
    }),
    'sourcing_template.not_found': defineMessage({
        message: `Couldn't find sourcing template`,
    }),
    'sourcing_template.missing_name': defineMessage({
        message: `Name provided is an empty string`,
    }),
    'sourcing_template.invalid_abc_classification_settings': defineMessage({
        message: `ABC classification percentages must sum to 100 (both cost and items)`,
    }),
    'sourcing_template.tried_to_delete_default': defineMessage({
        message: 'The default sourcing template cannot be deleted',
    }),
    'sourcing_settings.invalid_abc_classification_settings': defineMessage({
        message: `ABC classification percentages must sum to 100 (both cost and items)`,
    }),
    'demand_group.cannot_delete_referenced': defineMessage({
        message:
            'Cannot delete while sourcing scenarios are using this demand group. Remove all sourcing scenarios first.',
    }),
    'demand_group.conflicting_ship_to_sites': defineMessage({
        message: 'All demands in a demand group must share the same ship-to site',
    }),
    'demand_scenario.missing_annual_demand': defineMessage({
        message: 'Missing annual demand for demand scenario',
    }),
    'user.invalid_email': defineMessage({
        message: 'The email address is invalid. Please enter a valid email address.',
    }),
    // rust-workspace/domain-error/src/user.rs
    'user.invalid_first_name': defineMessage({
        message: 'Invalid first name',
    }),
    'user.invalid_last_name': defineMessage({
        message: 'Invalid last name',
    }),
    'user.invalid_password': defineMessage({
        message: 'The password does not comply with our guidelines. Please enter a valid email password.',
    }),
    // rust-workspace/domain-error/src/auth.rs
    'user_invite.internal_role_for_external_user': defineMessage({
        message: 'Cannot assign internal role to an external user',
    }),
    // rust-workspace/domain-error/src/user.rs
    'user.not_found': defineMessage({
        message: 'User not found',
    }),
    unknown: defineMessage({
        message: 'Sorry, there was a problem. Please try again later or contact our customer support.',
    }),

    unprocessable_entity: defineMessage({
        message: 'Sorry, there was a problem. Please try again later or contact our customer support.',
    }),
    // services/gateway/src/middlewares/middlewareError.ts
    bad_request: defineMessage({
        message: 'Sorry, there was a problem. Please try again later or contact our customer support.',
    }),
    too_large: defineMessage({
        message: 'Sorry, there was a problem. Please try again later or contact our customer support.',
    }),
    'off_the_shelf.duplicated_mpn': defineMessage({
        message: `Duplicated MPN`,
    }),
    'off_the_shelf.mpn_length_violation': defineMessage({
        message: `Must be 2 characters or more`,
    }),
    'off_the_shelf.package_violation': defineMessage({
        message: `Invalid package`,
    }),
    'off_the_shelf.part_already_exists': defineMessage({
        message: `Part already exists`,
    }),
    'offer_import_context.duplicate_name': defineMessage({
        message: 'Another offer import already has this name',
    }),
    'offer_import_context.in_use_by_offers': defineMessage({
        message: "This offer import still has offers, so it can't be deleted yet",
    }),
    'offer_import_context.internal': defineMessage({
        message: 'Something went wrong with the offer import. Try again.',
    }),
    'offer_import_context.not_found': defineMessage({
        message: "This offer import doesn't exist anymore",
    }),
    'offer_import_context.validation_failed': defineMessage({
        message: "The offer import's name or description is too long or has characters that aren't allowed",
    }),
    'customer_portal_settings.manufacturing_template_required': defineMessage({
        message: 'You must define a manufacturing template for the automatic order confirmation workflow.',
    }),
    'customer_portal_settings.calculation_template_required': defineMessage({
        message: 'You must define a calculation template for the automatic order confirmation workflow.',
    }),
    'customer_portal_settings.flexible_calculation_template_required': defineMessage({
        message: 'You must define a flexible calculation template for the automatic order confirmation workflow.',
    }),
    'customer_portal_settings.checkout_price_line_item_required': defineMessage({
        message: 'A checkout price line item must be selected when using flexible calculations.',
    }),
    'customer_portal_settings.checkout_price_line_item_not_in_template': defineMessage({
        message: 'The selected checkout price line item does not exist in the chosen flexible calculation template.',
    }),
    'customer_portal_settings.checkout_price_line_item_invalid': defineMessage({
        message:
            'The selected checkout price line item cannot be used (it must not be a Notes line item or display-only).',
    }),
    'customer_portal_settings.flexible_template_override_requires_flexible_org_mode': defineMessage({
        message:
            'A flexible calculation template override cannot be configured while the customer portal is using legacy calculations. Switch the customer portal to flexible calculations first.',
    }),
    'customer_portal_settings.template_in_use_by_customer_portal': defineMessage({
        message:
            'This template is configured as the customer-portal calculation template. Change the customer-portal setting before deleting it.',
    }),
    'customer_portal_settings.template_in_use_by_customer_specific_settings': defineMessage({
        message:
            'This template is in use by one or more customer-specific overrides. Remove those overrides before deleting it.',
    }),
    'customer_portal_settings.invalid_auto_rfq_number_range': defineMessage({
        message: 'the start of the range must be less than end',
    }),
    'payment.no_configured_payment_processor': defineMessage({
        message: 'Your payment processor is not configured.',
    }),
    'payment.not_found': defineMessage({
        message: 'Payment not found.',
    }),
    'payment.refunded': defineMessage({
        message: 'Payment has been refunded.',
    }),
    'payment.already_scheduled_for_refunded': defineMessage({
        message: 'Already scheduled for refund',
    }),
    'payment.scheduled_for_refunded': defineMessage({
        message: 'Payment is scheduled for refund',
    }),
    'payment.amount_mismatch': defineMessage({
        message: 'Amount mismatch',
    }),
    'payment.invalid_currency': defineMessage({
        message: 'Invalid currency',
    }),
    'order.tax_id_invalid': defineMessage({
        message: 'Invalid Tax ID. Please reach out to our support team to update your information.',
    }),
    'order.payment_method_not_allowed': defineMessage({
        message: 'Payment method not allowed.',
    }),
    'order.not_found': defineMessage({
        message: 'Order not found',
    }),
    'order.sourcing_offers_pending': defineMessage({
        message: 'Sourcing offers are pending',
    }),

    not_found: defineMessage({
        message: 'Record not found',
    }),
    'design_item.rfq_not_found': defineMessage({
        message: 'Project not found',
    }),
    'design_item.belong_to_different_rfq': defineMessage({
        message: 'Design items belongs to a different project',
    }),
    'design_item.not_found': defineMessage({
        message: 'Design item not found',
    }),
    'email.payload_too_large': defineMessage({
        message:
            'The email could not be sent because the attachments are too large. Please reduce the size of the attachments and try again.',
    }),
    'email.other_error': defineMessage({
        message: 'An unexpected error occurred while sending the email. Please try again later.',
    }),
    'flexible_calculation.locked_item_cannot_be_modified': defineMessage({
        message: 'This item or section is locked and cannot be modified.',
    }),
    'frozen_scenario_cost.already_frozen': defineMessage({
        message: 'Calculation is already frozen, please refresh the page',
    }),

    'auto_rfq_number_settings.cant_increment_manual_rfq_number': defineMessage({
        message: 'Trying to increment project number when settings are set to manual',
    }),
    'auto_rfq_number_settings.invalid_auto_rfq_number_range': defineMessage({
        message: 'Invalid range for automatic project number',
    }),
    'total_cost_of_ownership_rule.absolute_cost_cannot_be_negative': defineMessage({
        message: 'A total cost of ownership rule must have a positive absolute cost',
    }),
    'total_cost_of_ownership_rule.conditions_cannot_be_empty': defineMessage({
        message: 'A total cost of ownership rule must have at least one condition',
    }),
    'total_cost_of_ownership_rule.invalid_name': defineMessage({
        message: 'A total cost of ownership rule must have a name',
    }),
    'total_cost_of_ownership_rule.relative_cost_must_be_percentage': defineMessage({
        message: 'A total cost of ownership rule must have a percentage between 0 and 1000',
    }),
    'total_cost_of_ownership_rule.invalid_customs_code': defineMessage({
        message: 'A total cost of ownership rule must have a valid customs code format',
    }),
    'award_scenario.name_conflict': defineMessage({
        message: 'A scenario with this name already exists',
    }),
    'supplier_contact.contact_has_associated_user': defineMessage({
        message: 'Contact cannot be deleted because it is connected to a supplier portal user',
    }),
    'supplier_contact.name_invalid': defineMessage({
        message: 'Name is invalid',
    }),
    'supplier_contact.user_language_not_supported': defineMessage({
        message: 'User Language not supported',
    }),
    'supplier_contact.unknown_supplier': defineMessage({
        message: 'Unknown supplier',
    }),
    // rust-workspace/domain-error/src/user.rs
    'user.customer_id_missing': defineMessage({
        message: 'Customer ID is missing for this user',
    }),
    'user.failed_to_create_user': defineMessage({
        message: 'Failed to create user',
    }),
    'user.has_made_a_payment': defineMessage({
        message: 'This user has already made a payment. Please contact support to remove the user.',
    }),
    'supplier_contact.email_already_exists_as_user': defineMessage({
        message: 'This email is already associated with an existing user account.',
    }),
    'supplier_contact.unique_email_supplier_and_stock_location_violation': defineMessage({
        message: 'This email already exists for another user or supplier contact.',
    }),
    'quote_request.contacts_with_different_suppliers': defineMessage({
        message: 'The selected contacts belong to different suppliers. All contacts must be from the same supplier.',
    }),
    'quote_request.negotiation_line_items_not_found': defineMessage({
        message: 'No negotiation line items could be found.',
    }),
    'quote_request.invalid_data': defineMessage({
        message: 'Invalid data in quote request.',
    }),
    'quote_request.missing_user': defineMessage({
        message: 'Unable to find a user to be the sender of the quote request.',
    }),
    'quote_request.excel_generation_failure': defineMessage({
        message: 'Failed to generate quote request excel file.',
    }),
    'quote_request.draft_already_in_project': defineMessage({
        message: 'Similar draft quote request already exists in project.',
    }),
    'quote_request.cannot_approve_without_offer': defineMessage({
        message: 'Cannot approve quote request line items that have no received offer.',
    }),
    'quote_request.invalid_approval_status_transition': defineMessage({
        message: 'Invalid review status transition.',
    }),
    'quote_request.supplier_cannot_approve': defineMessage({
        message: 'Suppliers cannot set the review status to Approved or Pending.',
    }),
    'quote_request.approval_status_notes_too_long': defineMessage({
        message: 'Review status notes exceed the maximum allowed length.',
    }),
    'quote_request.cannot_send_empty_quote_request': defineMessage({
        message: 'Cannot send an empty quote request',
    }),
    'quote_request.cannot_send_in_current_status': defineMessage({
        message: 'Quote request cannot be sent in its current status.',
    }),
    'quote_request.no_email_recipients': defineMessage({
        message: 'No email recipients were provided.',
    }),
    'quote_request.multiple_emails_for_quote_request': defineMessage({
        message: 'Multiple emails were provided for a single quote request.',
    }),
    'quote_request.not_found': defineMessage({
        message: 'Quote request not found.',
    }),
    'quote_request.multiple_quote_requests_not_sent_for_supplier': defineMessage({
        message: 'Multiple quote requests not sent for the same supplier.',
    }),
    'quote_request.file_not_found': defineMessage({
        message: 'Quote request file not found.',
    }),
    'quote_request.supplier_contact_without_user': defineMessage({
        message: 'The supplier contact does not have a user. Please contact our customer support.',
    }),
    'quote_request.invalid_email_body': defineMessage({
        message: 'Email body cannot be empty',
    }),
    'quote_request.invalid_email_subject': defineMessage({
        message: 'Email subject cannot be empty',
    }),
    // Frontend only error code - used for assertSuccessfulResponseGenericErrorCode.ts

    'frontend_error.export.generic_error': defineMessage({
        message: 'An error occurred while exporting the data.',
    }),

    'auth.missing_auth_info': defineMessage({ message: 'Missing auth info' }),
    'auth.tenant_not_set_in_session': defineMessage({
        message: 'Tenant not set in session',
    }),
    'auth.must_be_customer_user': defineMessage({
        message: 'User must be a customer user',
    }),
    'auth.invalid_role': defineMessage({ message: 'Invalid role ID' }),
    'auth.role_conflict': defineMessage({
        message: 'Cannot assign role: User would be assigned conflicting roles',
    }),
    'pcb_specification_template.not_found': defineMessage({
        message: 'PCB specification template not found',
    }),
    'pcb_specification_template.customer_already_assigned': defineMessage({
        message: 'One or more customers are already assigned to a PCB specification template',
    }),
    'custom_part_category.duplicate_name': defineMessage({
        message: 'A category with this name or number already exists.',
    }),
    'custom_part_category.cannot_create_system_or_import_category': defineMessage({
        message: 'Cannot create System or Import category',
    }),
    'custom_part_category.cannot_edit_system_or_import_category': defineMessage({
        message: 'Cannot edit System or Import category',
    }),
    'custom_part_category.cannot_delete_system_or_import_category': defineMessage({
        message: 'Cannot delete System or Import category',
    }),
    'custom_part_category.depth_exceeds_maximum': defineMessage({
        message: 'Cannot create or edit custom part category exceeding depth of 10 levels',
    }),
    'custom_part_category.not_found': defineMessage({
        message: 'Custom part category not found',
    }),
    'custom_part_category.parent_not_found': defineMessage({
        message: 'Parent custom part category not found',
    }),
    'custom_part_category.validation_error': defineMessage({
        message: 'Custom part category validation error',
    }),

    // additional_service
    'additional_service.not_found': defineMessage({ message: 'Additional service not found' }),

    // assembly
    'assembly_api_imported_origin.invalid_json_format': defineMessage({
        message: 'Invalid JSON format for API imported assembly',
    }),
    'assembly.api_imported_assembly_missing_import_hash': defineMessage({
        message: 'API imported assembly is missing import hash',
    }),
    'assembly.cannot_delete_top_level_assembly_as_customer_user': defineMessage({
        message: 'You cannot delete top-level assemblies',
    }),
    'assembly.internal': defineMessage({ message: 'Internal assembly error' }),
    'assembly.monitoring_subscriber_is_not_ems': defineMessage({
        message: 'Monitoring subscriber is not an EMS user',
    }),
    'assembly.parents_not_found': defineMessage({ message: 'Assembly parents not found' }),
    'assembly.pcb_not_found_for_assembly': defineMessage({ message: 'PCB not found for assembly' }),
    'assembly.rfq_not_found_in_assembly': defineMessage({ message: 'Project not found for assembly' }),

    // auth
    'auth.must_use_password_auth': defineMessage({ message: 'Must use password authentication' }),

    // award_scenario
    'award_scenario.action_not_supported_on_award_scenario': defineMessage({
        message: 'This action is not supported on award scenarios',
    }),
    'award_scenario.invalid_awarded_price_types': defineMessage({
        message: 'Invalid awarded price types',
    }),
    'award_scenario.invalid_data': defineMessage({ message: 'Invalid award scenario data' }),
    'award_scenario.no_offer': defineMessage({ message: 'No offer found for award scenario' }),

    // comment
    'comment.external_user_cannot_create_internal_comment': defineMessage({
        message: 'External users cannot create internal comments',
    }),
    'comment.max_content_length': defineMessage({ message: 'Comment exceeds maximum content length' }),
    'comment.no_entity_found': defineMessage({ message: 'No entity found for comment' }),
    'comment.not_found': defineMessage({ message: 'Comment not found' }),

    // custom_part
    'custom_part_offer.conversion_failed': defineMessage({ message: 'Custom part offer conversion failed' }),
    'custom_part_offer.invalid_db_state': defineMessage({ message: 'Invalid database state for custom part offer' }),
    'custom_part_offer.invalid_payment_terms': defineMessage({
        message: 'Invalid payment terms for custom part offer',
    }),
    'custom_part_offer.missing_custom_part_specification': defineMessage({
        message: 'Missing custom part specification',
    }),
    'custom_part_specification.invalid_db_state': defineMessage({
        message: 'Invalid database state for custom part specification',
    }),
    'custom_part_specification.cannot_update_custom_part_to_pcb': defineMessage({
        message: 'Cannot convert custom part specification to PCB specification',
    }),
    'custom_part_specification.cannot_update_pcb_to_custom_part': defineMessage({
        message: 'Cannot convert PCB specification to custom part specification',
    }),
    'custom_part_specification.description_too_long': defineMessage({
        message: 'Custom part specification description is too long',
    }),
    'custom_part_specification.drawing_number_too_long': defineMessage({
        message: 'Custom part specification drawing number is too long',
    }),
    'custom_part_specification.revision_too_long': defineMessage({
        message: 'Custom part specification revision is too long',
    }),
    'custom_part_specification.sourcing_notes_too_long': defineMessage({
        message: 'Custom part specification sourcing notes are too long',
    }),
    'custom_part_specification.in_use_by_design_items': defineMessage({
        message: 'Custom part specification is in use and cannot be deleted',
    }),
    'custom_part.duplicate_supplier_part_number': defineMessage({
        message: 'Supplier part number already exists',
    }),

    // customer_portal
    'customer_portal_settings.internal': defineMessage({ message: 'Internal customer portal settings error' }),
    'customer_portal_settings.invoice_days_must_be_greater_zero': defineMessage({
        message: 'Invoice days must be greater than zero',
    }),
    'customer_portal_settings.minimum_quotations_lead_time_must_be_greater_than_zero': defineMessage({
        message: 'Minimum quotation lead time must be greater than zero',
    }),
    'customer_portal.checkout_price_unavailable': defineMessage({
        message: 'The checkout price could not be calculated',
    }),
    'customer_portal.internal': defineMessage({ message: 'Internal customer portal error' }),
    'customer_portal.single_top_level_assembly_not_found': defineMessage({
        message: 'Single top-level assembly not found',
    }),

    // customer
    'customer.hashing_failed': defineMessage({ message: 'Customer hashing failed' }),
    'customer.invalid_country_code': defineMessage({ message: 'Invalid country code' }),
    'customer.wrong_customer': defineMessage({ message: 'Wrong customer' }),

    // demand_scenario
    'demand_scenario.delivery_batch_size_out_of_bounds': defineMessage({
        message: 'Delivery batch size is out of bounds',
    }),
    'demand_scenario.delivery_date_not_allowed': defineMessage({ message: 'Delivery date is not allowed' }),
    'demand_scenario.invalid_annual_demand': defineMessage({ message: 'Invalid annual demand' }),
    'demand_scenario.invalid_assembly_id': defineMessage({ message: 'Invalid assembly ID' }),
    'demand_scenario.invalid_demand_scenario_end_year': defineMessage({
        message: 'Invalid demand scenario end year',
    }),
    'demand_scenario.invalid_demand_scenario_year': defineMessage({ message: 'Invalid demand scenario year' }),
    'demand_scenario.invalid_prototype_demand_scenario_quantity': defineMessage({
        message: 'Invalid prototype demand quantity',
    }),
    'demand_scenario.more_than_three_delivery_batch_sizes': defineMessage({
        message: 'Cannot have more than three delivery batch sizes',
    }),
    'demand_scenario.multiple_annual_demands_for_one_year': defineMessage({
        message: 'Multiple annual demands for one year',
    }),
    'demand_scenario.no_annual_demands': defineMessage({ message: 'No annual demands' }),
    'demand_scenario.prototype_demand_scenario_date_not_in_future': defineMessage({
        message: 'Prototype demand scenario date must be in the future',
    }),

    // demand
    'demand.assembly_demand_with_no_production_start_date': defineMessage({
        message: 'Assembly demand requires a production start date',
    }),
    'demand.internal': defineMessage({ message: 'Internal demand error' }),
    'demand.invalid_quantity': defineMessage({ message: 'Invalid demand quantity' }),
    'demand.missing_participant_site_information': defineMessage({
        message: 'Missing participant site information',
    }),
    'demand.no_calculated_demand_allowed_in_db': defineMessage({
        message: 'Calculated demand cannot be stored directly',
    }),
    'demand.production_start_date_required': defineMessage({ message: 'Production start date is required' }),
    'demand.supplier_is_not_ems': defineMessage({ message: 'Supplier is not an EMS' }),

    // design_item
    'design_item.incomplete_custom_option_information': defineMessage({
        message: 'Incomplete custom option information',
    }),
    'design_item.incomplete_part_suggestion': defineMessage({ message: 'Incomplete part suggestion' }),
    'design_item.incomplete_standard_part_information': defineMessage({
        message: 'Incomplete standard part information',
    }),
    'design_item.quantity_unit_conversion_error': defineMessage({ message: 'Quantity unit conversion error' }),

    // generic_part
    'generic_part.invalid_parameter': defineMessage({ message: 'Invalid generic part parameter' }),

    // history_of_changes
    'history_of_changes.invalid_creator': defineMessage({ message: 'Invalid creator in history of changes' }),
    'history_of_changes.invalid_history_operation': defineMessage({ message: 'Invalid history operation' }),

    // integration
    'integration.missing_api_key': defineMessage({ message: 'Missing API key for integration' }),

    // internal_auth
    'internal_auth.connection_name_mismatch': defineMessage({ message: 'Connection name mismatch' }),
    'internal_auth.connection_not_found': defineMessage({ message: 'Connection not found' }),

    // invite
    'invite.invalid_email': defineMessage({ message: 'Invalid email address' }),
    'invite.passwords_do_not_match': defineMessage({ message: 'Passwords do not match' }),

    // manufacturer
    'manufacturer_preference.preference_with_manufacturer_and_category_already_exists': defineMessage({
        message: 'A preference for this manufacturer and category already exists',
    }),
    'manufacturer_preference.preference_with_part_category_already_exists': defineMessage({
        message: 'A preference for this part category already exists',
    }),
    'manufacturer.alias_already_exists': defineMessage({
        message: 'This alias already exists for this manufacturer',
    }),
    'manufacturer.alias_conflicts_with_other_manufacturer': defineMessage({
        message: 'This name is already used by another manufacturer',
    }),
    'manufacturer.alias_repeats_manufacturer_name': defineMessage({
        message: "This is already the manufacturer's name",
    }),
    'manufacturer.duplicate_name': defineMessage({ message: 'A manufacturer with a similar name already exists' }),
    'manufacturer.empty_name': defineMessage({ message: 'Manufacturer name must not be empty' }),
    'manufacturer.empty_normalized_name': defineMessage({
        message: 'Manufacturer name contains only special characters',
    }),
    'manufacturer.invalid_data': defineMessage({ message: 'Invalid manufacturer data' }),
    'manufacturer.name_too_long': defineMessage({ message: 'Manufacturer name is too long' }),
    'manufacturer.unauthorized': defineMessage({ message: 'Not authorized to perform this manufacturer action' }),

    // negotiation
    'negotiation_part.invalid_data': defineMessage({ message: 'Invalid negotiation part data' }),
    'negotiation_v2_creation.demand_group_name_invalid': defineMessage({
        message: 'Demand group name must not be empty',
    }),
    'negotiation_v2_creation.duplicate_sourcing_scenario_name': defineMessage({
        message: 'Sourcing scenario names must be unique within a negotiation',
    }),
    'negotiation_v2_creation.name_invalid': defineMessage({
        message: 'Negotiation name must not be empty',
    }),
    'negotiation_v2_creation.sourcing_scenario_name_invalid': defineMessage({
        message: 'Sourcing scenario name must not be empty',
    }),
    'negotiation.different_negotiation': defineMessage({ message: 'Items belong to different negotiations' }),
    'negotiation.not_found': defineMessage({ message: 'Negotiation not found' }),

    // off_the_shelf
    'off_the_shelf_offer.not_found': defineMessage({ message: 'Off-the-shelf offer not found' }),
    'off_the_shelf.invalid_emission_data': defineMessage({ message: 'Invalid emission data' }),

    // offer
    'offer_origin.cannot_store_consigned': defineMessage({ message: 'Cannot store consigned offer origin' }),
    'offer.cannot_delete_public_off_the_shelf_offers': defineMessage({
        message: 'Public off-the-shelf offers cannot be deleted',
    }),
    'offer.cannot_delete_referenced_offers': defineMessage({
        message: 'The offer is referenced in sourcing and cannot be deleted',
    }),
    'offer.early_payment_due_days_must_be_positive': defineMessage({
        message: 'Early payment due days must be greater than 0',
    }),
    'offer.invalid_db_state': defineMessage({ message: 'Invalid database state for offer' }),
    'offer.invalid_off_the_shelf_id': defineMessage({ message: 'Invalid off-the-shelf ID' }),
    'offer.invalid_payment_terms': defineMessage({ message: 'Invalid payment terms' }),
    'offer.net_payment_due_days_must_be_positive': defineMessage({
        message: 'Net payment due days must be greater than 0',
    }),
    'offer.not_found': defineMessage({ message: 'Offer not found' }),

    // order
    'order.internal': defineMessage({ message: 'Internal order error' }),
    'order.order_selection_options_not_found': defineMessage({ message: 'Order selection options not found' }),
    'order.unauthorized': defineMessage({ message: 'Not authorized to perform this order action' }),

    // part_search
    'part_search.exclusive_filters': defineMessage({ message: 'Exclusive part search filters' }),
    'part_search.generic_part_to_search': defineMessage({ message: 'Generic part to search' }),

    // payload
    'payload.cannot_be_both_customer_and_supplier_user': defineMessage({
        message: 'A user cannot be both a customer and supplier user',
    }),
    'payload.customer_portal_invalid_assembly_type': defineMessage({
        message: 'Invalid assembly type for customer portal',
    }),
    'payload.customer_portal_invalid_pcb_workflow_settings': defineMessage({
        message: 'Invalid PCB workflow settings for customer portal',
    }),
    'payload.invoice_days_before_due_must_be_greater_than_zero': defineMessage({
        message: 'Invoice days before due must be greater than zero',
    }),
    'payload.lead_time_must_be_greater_than_zero': defineMessage({
        message: 'Lead time must be greater than zero',
    }),
    'payload.payment_id_cannot_be_null': defineMessage({ message: 'Payment ID is required' }),
    'payload.payment_processor_cannot_be_null': defineMessage({ message: 'Payment processor is required' }),
    'payload.private_key_cannot_be_empty': defineMessage({ message: 'Private key is required' }),
    'payload.public_key_cannot_be_empty': defineMessage({ message: 'Public key is required' }),
    'payload.standard_quotation_lead_time_days_cannot_be_null': defineMessage({
        message: 'Standard quotation lead time is required',
    }),
    'payload.tenant_cannot_be_empty': defineMessage({ message: 'Tenant is required' }),

    // pcb
    'pcb.json': defineMessage({ message: 'PCB JSON error' }),
    'pcb.panel': defineMessage({ message: 'PCB panel sync error' }),
    'pcb.reqwest': defineMessage({ message: 'PCB request error' }),

    // purchase_order
    'purchase_order.cannot_infer_sourcing_scenario': defineMessage({
        message: 'Cannot infer sourcing scenario for purchase order',
    }),
    'purchase_order.no_line_items': defineMessage({ message: 'Purchase order has no line items' }),
    'purchase_order.no_recipient': defineMessage({ message: 'Purchase order has no recipient' }),
    'purchase_order.no_sender': defineMessage({ message: 'Purchase order has no sender' }),
    'purchase_order.not_found': defineMessage({ message: 'Purchase order not found' }),

    // query
    'query.rfq_id_missing': defineMessage({ message: 'Project ID is missing from query' }),

    // quote_import
    'quote_import.custom_part_not_found': defineMessage({ message: 'Custom part not found' }),
    'quote_import.factory_lead_time_negative_or_zero': defineMessage({
        message: 'Factory lead time must be greater than zero',
    }),
    'quote_import.factory_quantity_negative': defineMessage({ message: 'Factory quantity must not be negative' }),
    'quote_import.invalid_payment_terms': defineMessage({ message: 'Invalid payment terms' }),
    'quote_import.invalid_unit_of_measurement': defineMessage({ message: 'Invalid unit of measurement' }),
    'quote_import.lead_time_negative_or_zero': defineMessage({
        message: 'Lead time must be greater than zero',
    }),
    'quote_import.line_items_not_found_for_quote_request': defineMessage({
        message: 'Line items not found for quote request',
    }),
    'quote_import.many_offers_for_single_quote_request_line_item': defineMessage({
        message: 'Multiple offers found for a single line item',
    }),
    'quote_import.moq_negative_or_zero': defineMessage({ message: 'MOQ must be greater than zero' }),
    'quote_import.mpq_negative_or_zero': defineMessage({ message: 'MPQ must be greater than zero' }),
    'quote_import.requested_off_the_shelf_part_not_found': defineMessage({
        message: 'Requested off-the-shelf part not found',
    }),
    'quote_import.stock_negative': defineMessage({ message: 'Stock must not be negative' }),
    'quote_import.supplier_and_stock_location_not_found': defineMessage({
        message: 'Supplier and stock location not found',
    }),
    'quote_import.unit_price_negative': defineMessage({ message: 'Unit price must not be negative' }),
    'quote_import.unsupported_part_type': defineMessage({ message: 'Unsupported part type' }),

    // quote_request_export
    'quote_request_export.comparison_error': defineMessage({ message: 'Quote request export comparison error' }),
    'quote_request_export.missing_value': defineMessage({ message: 'Missing value in quote request export' }),
    'quote_request_export.quantity_error': defineMessage({ message: 'Quantity error in quote request export' }),
    'quote_request_export.too_many_overlapping_solution_configurations': defineMessage({
        message: 'Too many overlapping solution configurations',
    }),

    // quote_request
    'quote_request.both_negotiation_line_items_and_solution_configuration_provided': defineMessage({
        message: 'Cannot provide both negotiation line items and solution configuration',
    }),
    'quote_request.custom_part_not_found': defineMessage({ message: 'Custom part not found' }),
    'quote_request.different_projects': defineMessage({ message: 'Items belong to different projects' }),
    'quote_request.different_rfq': defineMessage({ message: 'Items belong to different projects' }),
    'quote_request.factory_lead_time_negative_or_zero': defineMessage({
        message: 'Factory lead time must be greater than zero',
    }),
    'quote_request.factory_quantity_negative': defineMessage({ message: 'Factory quantity must not be negative' }),
    'quote_request.failed_to_duplicate_line': defineMessage({
        message: 'Failed to duplicate quote request line',
    }),
    'quote_request.failed_to_get_internal_rfq_contributors': defineMessage({
        message: 'Failed to get internal project contributors',
    }),
    'quote_request.failed_writing_attachments_to_zip': defineMessage({
        message: 'Failed to write attachments to ZIP file',
    }),
    'quote_request.incorrect_quote_request_line_item_unit': defineMessage({
        message: 'Incorrect unit for quote request line item',
    }),
    'quote_request.invalid_payment_terms': defineMessage({ message: 'Invalid payment terms' }),
    'quote_request.invalid_unit_of_measurement': defineMessage({ message: 'Invalid unit of measurement' }),
    'quote_request.lead_time_negative_or_zero': defineMessage({
        message: 'Lead time must be greater than zero',
    }),
    'quote_request.line_items_not_found_for_quote_request': defineMessage({
        message: 'Line items not found for quote request',
    }),
    'quote_request.many_offers_for_single_quote_request_line_item': defineMessage({
        message: 'Multiple offers found for a single line item',
    }),
    'quote_request.moq_negative_or_zero': defineMessage({ message: 'MOQ must be greater than zero' }),
    'quote_request.mpq_negative_or_zero': defineMessage({ message: 'MPQ must be greater than zero' }),
    'quote_request.no_negotiation_or_project': defineMessage({
        message: 'No negotiation or project associated',
    }),
    'quote_request.not_linked_to_valid_entity': defineMessage({
        message: 'Quote request is not linked to a valid entity',
    }),
    'quote_request.provided_rfq_and_sourcing_scenario_rfq_mismatch': defineMessage({
        message: 'Project mismatch between provided project and sourcing scenario',
    }),
    'quote_request.quote_request_line_item_not_found': defineMessage({
        message: 'Quote request line item not found',
    }),
    'quote_request.requested_off_the_shelf_part_not_found': defineMessage({
        message: 'Requested off-the-shelf part not found',
    }),
    'quote_request.sourcing_scenarios_missing_rfq': defineMessage({
        message: 'Sourcing scenarios are missing a project',
    }),
    'quote_request.stock_negative': defineMessage({ message: 'Stock must not be negative' }),
    'quote_request.supplier_and_stock_location_not_found': defineMessage({
        message: 'Supplier and stock location not found',
    }),
    'quote_request.unit_price_negative': defineMessage({ message: 'Unit price must not be negative' }),
    'quote_request.unsupported_part_type': defineMessage({ message: 'Unsupported part type' }),

    // refund
    'refund.already_refunded': defineMessage({ message: 'Already refunded' }),
    'refund.already_scheduled_for_refund': defineMessage({ message: 'Already scheduled for refund' }),
    'refund.duplicate_transaction': defineMessage({ message: 'Duplicate refund transaction' }),

    // resource
    'resource.name_already_exists': defineMessage({ message: 'Resource name already in use' }),
    'resource.not_found': defineMessage({ message: 'Resource not found' }),

    // snapshot
    'snapshot.notes_too_long': defineMessage({ message: 'Snapshot notes are too long' }),

    // solution_configuration
    'solution_configuration.cannot_set_manual_costs_on_automatic_solution': defineMessage({
        message: 'Cannot set manual costs on automatic solution',
    }),
    'solution_configuration.cannot_set_manual_solution_and_manual_sourced_by_at_the_same_time': defineMessage({
        message: 'Cannot set manual solution and manual sourced by at the same time',
    }),
    'solution_configuration.cannot_set_manual_solution_and_manual_status_at_the_same_time': defineMessage({
        message: 'Cannot set manual solution and manual status at the same time',
    }),
    'solution_configuration.invalid_selection': defineMessage({ message: 'Invalid solution selection' }),
    'solution_configuration.offer_loading_error': defineMessage({ message: 'Error loading offer for solution' }),
    'solution_configuration.part_demands_not_supported': defineMessage({
        message: 'Part demands not supported for this solution',
    }),

    // sourcing_scenario
    'sourcing_scenario.cannot_find_sourcing_settings': defineMessage({
        message: 'Cannot find sourcing settings',
    }),
    'sourcing_scenario.data_source_both_included_and_excluded': defineMessage({
        message: 'A data source can only be either included OR excluded',
    }),
    'sourcing_scenario.invalid_demand_data': defineMessage({ message: 'Invalid demand data' }),
    'sourcing_scenario.invalid_project_link': defineMessage({ message: 'Invalid project link' }),

    // sourcing_settings
    'sourcing_settings.can_not_find_default_sourcing_settings': defineMessage({
        message: 'Cannot find default sourcing settings',
    }),
    'sourcing_settings.failed_to_convert_to_upsert': defineMessage({
        message: 'Failed to convert sourcing settings',
    }),
    'sourcing_settings.got_invalid_currency': defineMessage({ message: 'Invalid currency in sourcing settings' }),
    'sourcing_settings.invalid_offer_validation_window': defineMessage({
        message: 'Invalid offer validation window',
    }),
    'sourcing_settings.invalid_site_id_update': defineMessage({ message: 'Invalid site ID update' }),
    'sourcing_settings.invalid_sourcing_settings_id': defineMessage({ message: 'Invalid sourcing settings ID' }),
    'sourcing_settings.invalid_supplier_and_stock_location_id_update': defineMessage({
        message: 'Invalid supplier and stock location ID update',
    }),
    'sourcing_settings.multiple_solution_preferences': defineMessage({
        message: 'Multiple solution preferences found',
    }),
    'sourcing_settings.no_upsert_result': defineMessage({ message: 'No upsert result for sourcing settings' }),
    'sourcing_settings.not_found': defineMessage({ message: 'Sourcing settings not found' }),
    'sourcing_settings.tried_to_insert_non_approved_supplier': defineMessage({
        message: 'Cannot add non-approved supplier',
    }),

    // sourcing_template
    'sourcing_template.data_source_both_included_and_excluded': defineMessage({
        message: 'A data source can only be either included OR excluded',
    }),
    'sourcing_template.failed_to_find_default': defineMessage({ message: 'Failed to find default sourcing template' }),
    'sourcing_template.failed_to_find_sourcing_schema_default': defineMessage({
        message: 'Failed to find default sourcing schema',
    }),
    'sourcing_template.failed_to_find_sourcing_settings': defineMessage({
        message: 'Failed to find sourcing settings for template',
    }),

    // stock_location
    'stock_location.not_found': defineMessage({ message: 'Stock location not found' }),

    // supplier_contact
    'supplier_contact.cannot_edit_public_supplier_contact': defineMessage({
        message: 'Cannot edit public supplier contact',
    }),
    'supplier_contact.invalid_main_contact': defineMessage({ message: 'Invalid main contact' }),
    'supplier_contact.multiple_main_contact_insertions': defineMessage({
        message: 'Cannot insert multiple main contacts',
    }),
    'supplier_contact.only_contact_must_remain_main': defineMessage({
        message: 'The only remaining contact must be the main contact',
    }),
    'supplier_contact.phone_number_not_allowed_on_public_contact': defineMessage({
        message: 'Public supplier contacts cannot have a phone number',
    }),

    // supplier
    'supplier.duplicate_supplier_number': defineMessage({ message: 'Supplier number already in use' }),
    'supplier.invalid_update_on_system_supplier': defineMessage({
        message: 'Cannot update system supplier',
    }),
    'supplier.public_supplier_by_tenant_deletion': defineMessage({
        message: 'Cannot delete public supplier',
    }),

    // total_cost_of_ownership
    'total_cost_of_ownership_rule.not_found': defineMessage({ message: 'Total cost of ownership rule not found' }),
};
